import { SegmentedControl, IconButton } from '@hue/design-system';
import {
  Button,
  CodeEditor,
  Input,
  Modal,
  Select,
  StatusPill,
  StatusText,
  Textarea,
  useIsWideViewport,
  useSnackbar,
  type StatusTone,
} from '@hue/design-system';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  History,
  Mail,
  Monitor,
  RotateCcw,
  Send,
  Smartphone,
  Trash2,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  companyEmailTemplatesApi,
  emailTemplatesApi,
  getApiError,
  getTemplateIssues,
  type TemplateDetail,
  type TemplateDraft,
  type TemplateField,
  type TemplateIssue,
  type TemplatePreview,
  type TemplatesApi,
  type TemplateStatus,
  type TemplateVersion,
} from '../lib/api.js';
import { formatDateTime } from '../lib/time-zone.js';
import type { TemplatesView } from './list-views.js';
import type { ViewSetter } from './view-state.js';
import { ErrorCard, LoadingCard } from './data-view.js';
import { PageHeader } from './page-title.js';

/// Quanto o editor espera antes de perguntar ao servidor como ficou.
///
/// A prévia é uma chamada de rede a cada parada de digitação. Meio segundo é o intervalo em que
/// quem escreve já parou de pensar na palavra e ainda não começou a esperar.
const PREVIEW_DELAY_MS = 500;

const statusLabels: Record<TemplateStatus, { label: string; tone: StatusTone }> = {
  DRAFT: { label: 'Rascunho', tone: 'progress' },
  PUBLISHED: { label: 'No ar', tone: 'positive' },
  ARCHIVED: { label: 'Arquivada', tone: 'neutral' },
};

/// O que esta pessoa pode fazer com o texto. No console root é tudo; dentro de uma empresa, é o que
/// o perfil dela concede — e é por isso que os botões somem em vez de falharem no clique.
export interface TemplateCapabilities {
  canEdit: boolean;
  canPublish: boolean;
  canTest: boolean;
}

const everything: TemplateCapabilities = { canEdit: true, canPublish: true, canTest: true };

/// Os modelos de e-mail: o padrão da plataforma, ou o texto de uma empresa.
///
/// Antes desta tela, trocar uma palavra num convite era editar um arquivo e subir código — o que,
/// na prática, queria dizer que o texto nunca mudava. A mesma tela serve aos dois lados porque a
/// tarefa é idêntica; o que muda é de quem é o texto e quem pode mexer nele.
export function EmailTemplates({
  timeZone,
  scope = 'PLATFORM',
  capabilities = everything,
  view,
  onView,
}: {
  timeZone: string;
  scope?: 'PLATFORM' | 'TENANT';
  capabilities?: TemplateCapabilities;
  view: TemplatesView;
  onView: ViewSetter<TemplatesView>;
}) {
  const api = scope === 'TENANT' ? companyEmailTemplatesApi : emailTemplatesApi;

  const list = useQuery({ queryKey: ['email-templates', scope], queryFn: api.list });
  const events = list.data?.templates ?? [];
  const known = events.some((item) => item.event === view.event);
  const current = known ? view.event : events[0]?.event;

  /// Endereço apontando para um modelo que não existe — link antigo, evento renomeado, chave
  /// digitada à mão — cai no primeiro **e limpa o endereço**. Mostrar o primeiro com a URL ainda
  /// dizendo outra coisa deixaria a tela discordando do endereço, que é pior do que não ter
  /// endereço nenhum.
  useEffect(() => {
    if (view.event && events.length > 0 && !known) onView({ event: '' }, { replace: true });
  }, [view.event, events.length, known, onView]);

  if (list.isLoading) return <LoadingCard label="Carregando modelos de e-mail" />;
  if (list.error) return <ErrorCard message={getApiError(list.error)} />;

  return (
    <section aria-labelledby="email-templates-title">
      <PageHeader
        trail={
          scope === 'TENANT'
            ? 'Configurações / Modelos de e-mail'
            : 'Plataforma / Comunicação / Modelos'
        }
        id="email-templates-title"
        title="Modelos de e-mail"
        description={
          scope === 'TENANT'
            ? 'O texto dos e-mails que a sua empresa manda. Enquanto você não publicar o seu, vale o texto padrão do sistema.'
            : 'O texto de cada e-mail que o sistema manda. Publicar cria uma versão nova; a que estava no ar fica guardada, e voltar atrás é publicá-la de novo.'
        }
      />

      {events.length === 0 ? (
        <p className="text-sm text-content-muted">
          Nenhum e-mail desta empresa tem texto editável por enquanto.
        </p>
      ) : (
        <nav aria-label="Eventos de e-mail" className="mb-6 flex flex-wrap gap-2">
          {events.map((item) => (
            <button
              key={item.event}
              type="button"
              aria-current={item.event === current ? 'true' : undefined}
              onClick={() => {
                onView({ event: item.event });
              }}
              className={
                item.event === current
                  ? 'rounded-xl border border-brand-500/60 bg-brand-500/10 px-3.5 py-2 text-sm font-medium text-content'
                  : 'rounded-xl border border-surface-border px-3.5 py-2 text-sm text-content-muted transition hover:border-brand-500/40 hover:text-content'
              }
            >
              {item.label}
              {item.hasDraft ? (
                <span className="ml-2 rounded-full bg-info/20 px-2 py-0.5 text-[11px] text-info">
                  rascunho
                </span>
              ) : null}
            </button>
          ))}
        </nav>
      )}

      {current ? (
        <TemplateEditor
          key={current}
          event={current}
          timeZone={timeZone}
          scope={scope}
          api={api}
          capabilities={capabilities}
        />
      ) : null}
    </section>
  );
}

function TemplateEditor({
  event,
  timeZone,
  scope,
  api,
  capabilities,
}: {
  event: string;
  timeZone: string;
  scope: 'PLATFORM' | 'TENANT';
  api: TemplatesApi;
  capabilities: TemplateCapabilities;
}) {
  const client = useQueryClient();
  const snackbar = useSnackbar();
  const detail = useQuery({
    queryKey: ['email-template', scope, event],
    queryFn: () => api.detail(event),
  });

  const [draft, setDraft] = useState<TemplateDraft>();
  const [notes, setNotes] = useState('');
  // Quem edita num celular vê a prévia estreita primeiro: a de 600px mostraria, em 320, um terço do
  // e-mail e dois terços de branco. Só o valor inicial — trocar depois é escolha de quem está
  // olhando, e virar a tela não desfaz a escolha dela.
  const [width, setWidth] = useState<'desktop' | 'mobile'>(
    useIsWideViewport() ? 'desktop' : 'mobile',
  );
  const [history, setHistory] = useState(false);
  const loaded = useRef<string | undefined>(undefined);

  // O editor abre no rascunho, ou numa cópia do que está no ar. Abrir em branco obrigaria a
  // reescrever a mensagem inteira para trocar uma palavra.
  useEffect(() => {
    if (!detail.data || loaded.current === event) return;
    loaded.current = event;
    setDraft({
      subject: detail.data.editing.subject,
      bodyHtml: detail.data.editing.bodyHtml,
      bodyText: detail.data.editing.bodyText,
    });
    setNotes(detail.data.editing.notes ?? '');
  }, [detail.data, event]);

  const settled = useDebounced(draft);
  const preview = useQuery({
    queryKey: ['email-template-preview', scope, event, settled],
    queryFn: () => api.preview(event, settled as TemplateDraft),
    enabled: settled !== undefined,
    placeholderData: keepPreviousData,
    retry: false,
  });

  // Os problemas são o próprio erro da prévia, lido a cada render — e não um estado à parte. Uma
  // cópia em `useState` conseguiria discordar do que está na tela, que é exatamente o que uma lista
  // de erros não pode fazer.
  const issues = preview.error ? getTemplateIssues(preview.error) : [];

  function refresh(updated: TemplateDetail) {
    client.setQueryData(['email-template', scope, event], updated);
    void client.invalidateQueries({ queryKey: ['email-templates', scope] });
  }

  /// Toda ação daqui fala pelo mesmo canal. Escrito uma vez porque são cinco mutações, e cinco
  /// cópias é como uma delas acaba esquecida e falhando calada.
  const onError = (error: Error) => snackbar.failure(getApiError(error));

  const save = useMutation({
    mutationFn: () => api.saveDraft(event, { ...(draft as TemplateDraft), notes: notes || null }),
    onSuccess: (updated) => {
      refresh(updated);
      snackbar.success('Rascunho salvo.');
    },
    onError,
  });

  const publish = useMutation({
    mutationFn: (version: number) => api.publish(event, version),
    onSuccess: (updated) => {
      refresh(updated);
      snackbar.success('Publicado. O próximo envio já sai com este texto.');
    },
    onError,
  });

  const restore = useMutation({
    mutationFn: (version: number) => api.restore(event, version),
    onSuccess: (updated) => {
      loaded.current = undefined;
      refresh(updated);
      setHistory(false);
      snackbar.success('Voltamos ao texto dessa versão, publicado com número novo.');
    },
    onError,
  });

  const discard = useMutation({
    mutationFn: () => api.discardDraft(event),
    onSuccess: (updated) => {
      loaded.current = undefined;
      refresh(updated);
      snackbar.success('Rascunho descartado.');
    },
    onError,
  });

  const test = useMutation({
    mutationFn: (version: number) => api.sendTest(event, version),
    onSuccess: (result) => snackbar.success(`Teste a caminho de ${result.to}.`),
    onError,
  });

  if (detail.isLoading || !draft) return <LoadingCard label="Carregando o modelo" />;
  if (detail.error) return <ErrorCard message={getApiError(detail.error)} />;
  const data = detail.data as TemplateDetail;

  const pending =
    save.isPending || publish.isPending || restore.isPending || discard.isPending || test.isPending;
  const savedVersion = data.editing.version;
  const canPublish = data.hasDraft && savedVersion !== null && issues.length === 0;

  function update(field: keyof TemplateDraft, value: string) {
    setDraft((current) => ({ ...(current as TemplateDraft), [field]: value }));
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        {/* Numa empresa que ainda não escreveu o texto dela, "Nada publicado" sozinho pareceria um
            e-mail saindo sem conteúdo. O que sai é o padrão do sistema, e a tela diz isso. */}
        <StatusPill tone={data.sendingOrigin === 'TENANT' ? 'positive' : 'neutral'}>
          {data.sendingOrigin === 'TENANT'
            ? `Versão ${String(data.publishedVersion ?? 0)} da empresa no ar`
            : scope === 'TENANT'
              ? 'Saindo com o texto padrão do sistema'
              : data.publishedVersion === null
                ? 'Nada publicado'
                : `Versão ${String(data.publishedVersion)} no ar`}
        </StatusPill>
        {data.hasDraft ? <StatusPill tone="progress">Rascunho não publicado</StatusPill> : null}
        <Button variant="outline" onClick={() => setHistory(true)}>
          <History className="size-4" />
          Versões ({String(data.versions.length)})
        </Button>
      </div>

      <Input
        label="Assunto"
        value={draft.subject}
        readOnly={!capabilities.canEdit}
        onChange={(changed) => update('subject', changed.target.value)}
        error={messageFor(issues, 'subject')}
        hint="É o que aparece na caixa de entrada. Texto puro: marcação aqui chega como está escrita."
      />

      {/* A prévia manda na largura, e não a metade da tela: 600px é a medida do e-mail, e encolhê-la
          para caber num layout meio a meio mostraria uma mensagem que ninguém vai receber assim. O
          editor fica com o que sobra. */}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_auto]">
        {/* `min-w-0` nos dois lados: item de grade se recusa a encolher abaixo do conteúdo, e sem
            isto a prévia de 600px empurraria a página inteira para o lado num celular de 320. */}
        <div className="min-w-0 space-y-4">
          <CodeEditor
            label="Corpo em HTML"
            value={draft.bodyHtml}
            onValueChange={(changed) => update('bodyHtml', changed)}
            readOnly={!capabilities.canEdit}
            errorLines={issues.filter((issue) => issue.field === 'bodyHtml').map((i) => i.line)}
            toolbar={<VariableList variables={data.variables} />}
            hint="A moldura do e-mail — cabeçalho, largura, logo — fica por conta do sistema. Aqui vai o miolo."
          />
          <Textarea
            label="Corpo em texto puro"
            rows={6}
            value={draft.bodyText}
            readOnly={!capabilities.canEdit}
            onChange={(changed) => update('bodyText', changed.target.value)}
            error={messageFor(issues, 'bodyText')}
            hint="Vai junto em toda mensagem: filtro de spam pune e-mail só-HTML, e é isto que um leitor de tela lê."
          />
          <Input
            label="O que mudou nesta versão"
            value={notes}
            readOnly={!capabilities.canEdit}
            onChange={(changed) => setNotes(changed.target.value)}
            hint="Aparece na lista de versões. Sem isso, escolher para qual voltar é abrir uma por uma."
          />
        </div>

        <Preview
          event={event}
          rendered={preview.data}
          fetching={preview.isFetching}
          width={width}
          onWidthChange={setWidth}
          issues={issues}
        />
      </div>

      {/* Quem só pode ler não vê botão nenhum. Botão que aparece e responde "você não pode" é uma
          promessa desfeita no clique — e escondê-lo é o mesmo recorte que a API confere de novo. */}
      {capabilities.canEdit || capabilities.canPublish || capabilities.canTest ? (
        <div className="flex flex-wrap gap-3 border-t border-surface-border pt-5">
          {capabilities.canEdit ? (
            <Button onClick={() => save.mutate()} disabled={pending || issues.length > 0}>
              <Check className="size-4" />
              Salvar rascunho
            </Button>
          ) : null}
          {capabilities.canTest ? (
            <Button
              variant="outline"
              onClick={() => savedVersion !== null && test.mutate(savedVersion)}
              disabled={pending || savedVersion === null}
            >
              <Send className="size-4" />
              Enviar teste para mim
            </Button>
          ) : null}
          {capabilities.canPublish ? (
            <Button
              onClick={() => savedVersion !== null && publish.mutate(savedVersion)}
              disabled={pending || !canPublish}
            >
              <Mail className="size-4" />
              Publicar
            </Button>
          ) : null}
          {capabilities.canEdit && data.hasDraft ? (
            <Button variant="outline" onClick={() => discard.mutate()} disabled={pending}>
              <Trash2 className="size-4" />
              Descartar rascunho
            </Button>
          ) : null}
        </div>
      ) : null}
      {/* O teste sai do que está gravado, não do que está na tela: a fila lê o texto do banco, e
          mandar o rascunho não salvo faria o teste mostrar uma coisa e o envio real, outra. */}
      {capabilities.canTest && savedVersion === null ? (
        <p className="text-xs text-content-muted">
          Salve o rascunho para poder enviar um teste: o teste sai pela fila de verdade, que lê o
          texto gravado.
        </p>
      ) : null}

      <Modal
        open={history}
        onOpenChange={setHistory}
        title="Versões deste e-mail"
        description="Cada publicação cria uma versão. As antigas ficam para sempre — é o que permite reler o que a pessoa recebeu."
      >
        <VersionHistory
          versions={data.versions}
          timeZone={timeZone}
          pending={restore.isPending}
          canRestore={capabilities.canPublish}
          onRestore={(version) => restore.mutate(version)}
        />
      </Modal>
    </div>
  );
}

/// Os dois corpos da mesma mensagem, um de cada vez.
///
/// Toda mensagem sai com os dois, e até aqui só o HTML tinha prévia — quem escrevia o texto puro
/// escrevia às cegas, o que é justamente como a metade não revisada da mensagem chega ao
/// destinatário. Um de cada vez, e não lado a lado: são 600px de e-mail contra uma coluna de
/// editor, e espremer os dois na mesma largura tiraria a única coisa que a prévia tem de fazer
/// direito, que é mostrar o tamanho de verdade.
type PreviewFormat = 'html' | 'text';

const previewFormats: ReadonlyArray<{ value: PreviewFormat; label: string }> = [
  { value: 'html', label: 'HTML' },
  { value: 'text', label: 'Texto puro' },
];

function Preview({
  event,
  rendered,
  fetching,
  width,
  onWidthChange,
  issues,
}: {
  event: string;
  rendered: TemplatePreview | undefined;
  fetching: boolean;
  width: 'desktop' | 'mobile';
  onWidthChange: (width: 'desktop' | 'mobile') => void;
  issues: TemplateIssue[];
}) {
  // Fica aqui, e não no editor: a largura precisa nascer do tamanho da tela, e o formato sempre
  // começa no HTML — é o corpo que quase todo mundo vai ver.
  const [format, setFormat] = useState<PreviewFormat>('html');
  const frameWidth = width === 'desktop' ? '600px' : '320px';

  return (
    <div className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <span className="pb-3 text-xs font-semibold text-content/70">
          Prévia{fetching ? ' · atualizando' : ''}
        </span>
        <div className="flex flex-wrap items-end gap-3">
          <SegmentedControl
            label="Corpo mostrado na prévia"
            compact
            className="mb-3"
            value={format}
            onChange={setFormat}
            options={previewFormats}
          />
          <Select
            label="Largura da prévia"
            className="w-40"
            value={width}
            onValueChange={(changed) => onWidthChange(changed as 'desktop' | 'mobile')}
            options={[
              { value: 'desktop', label: 'Computador' },
              { value: 'mobile', label: 'Celular' },
            ]}
          />
        </div>
      </div>

      {issues.length > 0 ? (
        <ul className="space-y-2 rounded-xl border border-danger/30 bg-danger/10 p-3 text-xs leading-5 text-danger">
          {issues.map((issue) => (
            <li key={`${issue.field}-${String(issue.line)}-${issue.message}`}>
              <span className="font-semibold">{fieldNames[issue.field]}</span>
              {issue.field === 'bodyHtml' ? `, linha ${String(issue.line)}` : ''}: {issue.message}
            </li>
          ))}
        </ul>
      ) : null}

      {rendered ? (
        <div className="space-y-3">
          {rendered.warnings.length > 0 ? (
            <ul className="space-y-1 rounded-xl border border-info/30 bg-info/10 p-3 text-xs leading-5 text-info">
              {rendered.warnings.map((warning) => (
                <li key={warning.message} className="flex gap-2">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                  <span>{warning.message}</span>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="overflow-hidden rounded-xl border border-surface-border bg-surface-card">
            <div className="flex items-center gap-2 border-b border-surface-border px-4 py-3">
              {width === 'desktop' ? (
                <Monitor className="size-4 text-content-faint" aria-hidden="true" />
              ) : (
                <Smartphone className="size-4 text-content-faint" aria-hidden="true" />
              )}
              <p className="min-w-0 truncate text-sm font-medium">{rendered.subject}</p>
            </div>
            <div className="overflow-x-auto bg-white">
              {format === 'html' ? (
                /* `sandbox` vazio: nenhum script, nenhum formulário, nenhuma navegação. O que está
                   sendo mostrado é texto de e-mail, e cliente de e-mail nenhum executaria script —
                   uma prévia que executasse mostraria algo que o destinatário nunca verá. */
                <iframe
                  key={`${event}-${width}`}
                  title="Prévia do e-mail"
                  srcDoc={rendered.html}
                  sandbox=""
                  className="block h-[36rem] border-0"
                  style={{ width: frameWidth }}
                />
              ) : (
                /* Na mesma largura do HTML, e não na coluna inteira: é assim que dá para ver que a
                   linha que cabia no computador vira três no celular. Sem moldura, porque o texto
                   puro não tem nenhuma — o que está aqui é a mensagem inteira que vai junto. */
                <pre
                  aria-label="Prévia do e-mail em texto puro"
                  className="m-0 block h-[36rem] overflow-y-auto whitespace-pre-wrap break-words px-6 py-5 font-mono text-xs leading-5 text-black/80"
                  style={{ width: frameWidth }}
                >
                  {rendered.text}
                </pre>
              )}
            </div>
          </div>
        </div>
      ) : issues.length === 0 ? (
        <LoadingCard label="Montando a prévia" />
      ) : null}
    </div>
  );
}

function VariableList({ variables }: { variables: string[] }) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      <span className="text-[11px] text-content-faint">Variáveis:</span>
      {variables.map((variable) => (
        <code
          key={variable}
          className="rounded bg-brand-500/15 px-1.5 py-0.5 font-mono text-[11px] text-brand-300"
        >
          {`{{${variable}}}`}
        </code>
      ))}
    </div>
  );
}

function VersionHistory({
  versions,
  timeZone,
  pending,
  canRestore,
  onRestore,
}: {
  versions: TemplateVersion[];
  timeZone: string;
  pending: boolean;
  canRestore: boolean;
  onRestore: (version: number) => void;
}) {
  if (versions.length === 0) {
    return <p className="text-sm text-content-muted">Este e-mail ainda não tem nenhuma versão.</p>;
  }
  return (
    <ol className="divide-y divide-surface-border">
      {versions.map((version) => (
        <li key={version.version} className="flex flex-wrap items-start gap-3 py-3">
          <div className="min-w-0 flex-1">
            <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
              Versão {version.version}
              <StatusText tone={statusLabels[version.status].tone} className="text-xs">
                {statusLabels[version.status].label}
              </StatusText>
            </p>
            {version.notes ? (
              <p className="mt-1 text-xs text-content-muted">{version.notes}</p>
            ) : null}
            <p className="mt-1 text-xs text-content-faint">
              {version.publishedAt
                ? `Publicada em ${formatDateTime(version.publishedAt, timeZone)}`
                : `Editada em ${formatDateTime(version.updatedAt, timeZone)}`}
            </p>
          </div>
          {canRestore && version.status === 'ARCHIVED' ? (
            <IconButton
              label="Voltar a esta"
              icon={<RotateCcw />}
              disabled={pending}
              onClick={() => onRestore(version.version)}
            />
          ) : null}
        </li>
      ))}
    </ol>
  );
}

const fieldNames: Record<TemplateField, string> = {
  subject: 'Assunto',
  bodyHtml: 'Corpo em HTML',
  bodyText: 'Texto puro',
};

function messageFor(issues: TemplateIssue[], field: TemplateField): string | undefined {
  return issues.find((issue) => issue.field === field)?.message;
}

/// Segura o valor até quem digita parar.
///
/// Sem isto, cada tecla viraria uma chamada de rede — e a prévia da palavra pela metade chegaria
/// depois da palavra inteira, piscando texto velho por cima do novo.
function useDebounced<T>(value: T, delay = PREVIEW_DELAY_MS): T {
  const [held, setHeld] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setHeld(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return held;
}
